// © Microsoft Corporation. All rights reserved.

import { ArticleCardHandledProps, ArticleCardProps, ArticleCardUnhandledProps } from "./ArticleCard.props";
import { HandledPropsKeys, ShallowCompare } from "@msnews/core";
import { Heading, Image, Typography } from "@microsoft/fast-components-react-msft";

import Foundation from "@microsoft/fast-components-foundation-react";
import React from "react";

/**
 * Article card state interface
 */
export interface ArticleCardState {}

/**
 * Article card component
 * @class - Article card
 * @classdesc - Article card component base; provides semantic structure of a basic article card.
 * @export
 * @default
 */
class ArticleCard extends Foundation<ArticleCardHandledProps, ArticleCardUnhandledProps, ArticleCardState> {
    /**
     * Article card handled props contract
     * @protected
     * @type { HandledPropsKeys<ArticleCardHandledProps>}
     */
    protected handledProps: HandledPropsKeys<ArticleCardHandledProps> = {
        anchorRef: undefined,
        headlineTitle: undefined,
        href: undefined,
        image: undefined,
        managedClasses: undefined,
        provider: undefined
    };

    /**
     * Construct a new article card
     * @constructor
     * @param { ArticleCardHandledProps } props the props contract for ArticleCard
     */
    constructor(props: ArticleCardHandledProps) {
        super(props);
    }

    /**
     * Performs a shallow comparison on the props
     * @param nextProps Incoming props
     * @param nextState Incoming state. Ignored as component is stateless
     */
    public shouldComponentUpdate(nextProps: ArticleCardProps): boolean {
        return !ShallowCompare(this.props, nextProps);
    }

    /**
     * Renders the component
     * @method
     * @returns {JSX.Element} the ArticleCard JSX element
     */
    public render(): JSX.Element {
        return (
            <div
                {...this.unhandledProps()}
                className={this.generateClassNames(this.props.managedClasses.articleCard)}
            >
                {this.renderContent()}
                {this.props.children}
            </div>
        );
    }

    /**
     * Renders the content component of the article
     * @method
     * @returns {JSX.Element} the content of an article
     */
    private renderContent(): JSX.Element {
        let content: JSX.Element;
        const { href, managedClasses } = this.props;

        if (href) {
            content = (
                <a
                    ref={this.props.anchorRef}
                    href={href}
                    className={managedClasses.articleCard_content}
                    target="_blank"
                >
                    {this.renderImage()}
                    <div className={managedClasses.articleCard_textWrapper}>
                        {this.renderTitle()}
                        {this.renderProvider()}
                    </div>
                </a>
            );
        } else {
            content = (
                <div className={managedClasses.articleCard_content}>
                    {this.renderImage()}
                    <div className={managedClasses.articleCard_textWrapper}>
                        {this.renderTitle()}
                        {this.renderProvider()}
                    </div>
                </div>
            );
        }

        return content;
    }

    /**
     * Renders the card image component of the article
     * @method
     * @returns {JSX.Element} the image component of an article
     */
    private renderImage(): JSX.Element {
        if (!this.props.image) {
            return;
        }

        const { articleCard_imageWrapper, articleCard_image, articleCard_imageOverlay } = this.props.managedClasses;

        return (
            <div className={articleCard_imageWrapper}>
                <Image
                    {...this.props.image}
                    className={articleCard_image}
                />
                <div className={articleCard_imageOverlay}/>
            </div>
        );
    }

    /**
     * Renders the headline title component of the article
     * @method
     * @returns {JSX.Element} the headline title component of an article
     */
    private renderTitle(): JSX.Element {
        const { articleCard_headline, articleCard_headlineText, articleCard_headlineMask } = this.props.managedClasses;

        return (
            <div className={articleCard_headline}>
                <Heading
                    {...this.props.headlineTitle}
                    className={articleCard_headlineText}
                />
                <div className={articleCard_headlineMask}/>
            </div>
        );
    }

    /**
     * Renders the article provider component of an article
     * @method
     * @returns {JSX.Element} the article provider component if it exists
     */
    private renderProvider(): JSX.Element {
        if (!this.props.provider) {
            return;
        }

        let providerImage: JSX.Element;
        if (this.props.provider.image && this.props.provider.image.src) {
            providerImage = (
                <Image
                    {...this.props.provider.image}
                    className={this.props.managedClasses.articleCard_providerImage}
                />
            );
        }

        return (
            <div className={this.props.managedClasses.articleCard_provider}>
                {providerImage}
                <Typography
                    {...this.props.provider.name}
                    className={this.props.managedClasses.articleCard_providerTitle}
                />
            </div>
        );
    }
}

export default ArticleCard;
export * from "./ArticleCard.props";